import React, { useState, useEffect } from 'react';
import { Input } from 'reactstrap';
import { Pencil, Save, X } from 'lucide-react';

interface CaptionEditorProps {
  fileWrapper: {
    id: string;
    name: string;
    caption?: string;
    url: string;
  };
  onSave: (id: string, caption: string) => Promise<void>;
  existingCaption?: string;
  isNewUpload?: boolean;
}

const CaptionEditor: React.FC<CaptionEditorProps> = ({
  fileWrapper,
  onSave,
  existingCaption = '',
  isNewUpload = false
}) => {
  const [isEditing, setIsEditing] = useState(isNewUpload || !existingCaption);
  const [caption, setCaption] = useState(existingCaption);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCaption(existingCaption);
  }, [existingCaption]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(fileWrapper.id, caption);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving caption:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCaption(existingCaption);
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <div className="caption-display flex items-center p-2 bg-gray-50 rounded">
        <span className="flex-grow text-gray-700">
          {existingCaption || 'Add a caption...'}
        </span>
        <button
          onClick={() => setIsEditing(true)}
          className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
          title="Edit caption"
        >
          <Pencil className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="caption-editor flex items-center gap-2 p-2">
      <Input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder={`Caption for ${fileWrapper.name}...`}
        className="flex-grow text-sm"
        disabled={isSaving}
        autoFocus
      />
      <div className="flex gap-1">
        <button
          onClick={handleSave}
          className="p-1 hover:bg-emerald-100 rounded-full transition-colors"
          title="Save caption"
          disabled={isSaving}
        >
          <Save className={`w-4 h-4 ${isSaving ? 'text-gray-400' : 'text-emerald-600'}`} />
        </button>
        {existingCaption && (
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-red-100 rounded-full transition-colors"
            title="Cancel editing"
            disabled={isSaving}
          >
            <X className="w-4 h-4 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CaptionEditor;